import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppConfig } from '../../../app.config';
import { RolePermission } from '../role/role.model';
import { LookupCriteria, ProvinceModel, ResetPasswordModel, UserModel, UserPreferences } from './user.model';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(private http: HttpClient, private toastrService: ToastrService) { }

  checkUserEmail(email: string) {
    return this.http.post<boolean>(`${AppConfig.settings.settings.apiEndpoint}api/User/CheckUserEmail`, { email: email })
      .pipe(map(value => {
        return value;
      }));
  }

  copyUser(id: number): Observable<UserModel> {
    return this.http.get<UserModel>(`${AppConfig.settings.settings.apiEndpoint}api/User/CopyUser/${id}`)
      .pipe(map(value => {
        if (value) {
          return value;
        }
        else {
          this.toastrService.error('Something Went Wrong', 'Error');
          return null;
        }
      }));
  }

  createOrUpdateUserDashboardPreferences(userPreferences: UserPreferences) {
    return this.http.post<boolean>(`${AppConfig.settings.settings.apiEndpoint}api/User/CreateOrUpdateUserDashboardPreferences`, userPreferences)
      .pipe(map(value => {
        return value;
      }));
  }

  createUser(userModel: UserModel): Observable<boolean> {
    return this.http.post<boolean>(`${AppConfig.settings.settings.apiEndpoint}api/User/CreateUser`, userModel)
      .pipe(map(value => {
        if (value) {
          this.toastrService.success(`${userModel.userName} Added Successfully`, 'Success');
        } else {
          this.toastrService.error(`${userModel.userName} Was Not Added`, 'Error');
        }

        return value ?? false;
      }));
  }

  getProvinces(): Observable<ProvinceModel[]> {
    return this.http.get<ProvinceModel[]>(`${AppConfig.settings.settings.apiEndpoint}api/User/Provinces`)
      .pipe(map(value => {
        return value;
      }));
  }

  // MOVE TO ROLE PERMISSION
  getRolePermissionByRoleAndPage(roleName: string, pageName: string): Observable<RolePermission> {
    return this.http.get<RolePermission>(`${AppConfig.settings.settings.apiEndpoint}api/RolePermission/GetRolePermissionByRoleAndPage?roleName=${roleName}&&pageName=${pageName}`)
      .pipe(map(value => {
        return value;
      }));
  }

  getUser(id: number): Observable<UserModel> {
    return this.http.get<UserModel>(`${AppConfig.settings.settings.apiEndpoint}api/User/${id}`)
      .pipe(map(value => {
        return value;
      }));
  }

  getUserDashboardPreferences(dashboardComponentName: string): Observable<string> {
    return this.http.get<string>(`${AppConfig.settings.settings.apiEndpoint}api/User/GetUserDashboardPreferences?dashboardComponentName=${dashboardComponentName}`)
      .pipe(map(value => {
        return value;
      }));
  }

  getUserProfile(): Observable<UserModel> {
    return this.http.get<UserModel>(`${AppConfig.settings.settings.apiEndpoint}api/User/GetUserProfile`)
      .pipe(map(value => {
        if (value) {
          return value;
        }
        else {
          this.toastrService.error('Something Went Wrong', 'Error');
          return null;
        }
      }));
  }

  lookupUsers(lookupCriteria: LookupCriteria): Observable<UserModel[]> {
    return this.http.post<UserModel[]>(`${AppConfig.settings.settings.apiEndpoint}api/User/LookupUsers`, lookupCriteria)
      .pipe(map(value => {
        return value;
      }));
  }

  // MOVE TO AUTH
  resetPassword(resetPasswordModel: ResetPasswordModel): Observable<boolean> {
    return this.http.post<boolean>(`${AppConfig.settings.settings.apiEndpoint}api/User/ResetPassword`, resetPasswordModel)
      .pipe(map(value => {
        if (value) {
          this.toastrService.success('User Password Reset Successfully', 'Success');
        }
        else {
          this.toastrService.error('Something Went Wrong', 'Error');
        }

        return value ?? false;
      }));
  }

  unlockUser(id: number): Observable<boolean> {
    return this.http.get<boolean>(`${AppConfig.settings.settings.apiEndpoint}api/User/UnlockUser?id=${id}`)
      .pipe(map(value => {
        if (value) {
          this.toastrService.success('User Unlocked Successfully', 'Success');
        }
        else {
          this.toastrService.error('Something Went Wrong', 'Error');
        }

        return value ?? false;
      }));
  }

  updateUser(userModel: UserModel): Observable<boolean> {
    return this.http.post<boolean>(`${AppConfig.settings.settings.apiEndpoint}api/User/UpdateUser`, userModel)
      .pipe(map(value => {
        if (value) {
          this.toastrService.success(`${userModel.userName} Updated Successfully`, 'Success');
        }
        else {
          this.toastrService.error(`${userModel.userName} Was Not Updated`, 'Error');
        }

        return value ?? false;
      }));
  }

  updateUserProfile(userModel: UserModel): Observable<boolean> {
    return this.http.post<boolean>(`${AppConfig.settings.settings.apiEndpoint}api/User/UpdateUserProfile`, userModel)
      .pipe(map(value => {
        if (value) {
          this.toastrService.success('Profile Updated Successfully', 'Success');
        }
        else {
          this.toastrService.error('Profile Was Not Updated', 'Error');
        }

        return value ?? false;
      }));
  }
}
